<template>
  <div :class="['SkeletonCard skeleton-card spark-card', { 'is-condensed': isVerticallyCondensed }]" aria-hidden="true">
    <div class="cover">
      <!-- Quiet fill behind seats; own node so nested seats keep their shimmer. -->
      <div class="cover-fill skeleton o-4" />

      <div class="cover-foot">
        <div class="cover-players">
          <div v-for="n in 4" :key="n" class="seat">
            <div class="seat-ring skeleton o-3" />
          </div>
        </div>
      </div>
    </div>

    <div class="chip-band">
      <div class="chip-skel is-wide skeleton o-3" />
      <div class="chip-skel skeleton o-3" />
    </div>

    <div class="meta">
      <!-- Neutral fill matching the cover; own node so the bars and CTA keep their shimmer. -->
      <div class="meta-fill skeleton o-4" />

      <div class="meta-head">
        <div class="when-skel skeleton o-1" />
        <div class="sub-skel skeleton o-2" />
      </div>

      <div class="controls">
        <div class="primary">
          <div class="action-button skeleton o-2" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isVerticallyCondensed } from '@/core/helpers/ui.helper';
</script>

<style scoped lang="scss">
.SkeletonCard {
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: var(--card-stack-gap);
  background: transparent;
  overflow: visible;
}

.SkeletonCard.spark-card {
  box-shadow: none;
}

.cover {
  position: relative;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  border-radius: var(--radius-normal);
  overflow: hidden;
  background: transparent;
}

.cover-fill {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.cover-foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem var(--card-gutter) var(--card-foot-inset);
}

.cover-players {
  display: flex;
  align-items: flex-end;
  gap: clamp(0.25rem, min(1.75cqw, 1.17cqh), 0.5rem);
}

.seat {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: var(--card-avatar-size);
}

.chip-band {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: var(--card-band-height);
}

.chip-skel {
  width: 3.5rem;
  height: 100%;
  border-radius: var(--radius-normal);

  &.is-wide {
    width: 6rem;
  }
}

.seat-ring {
  width: 100%;
  height: auto;
  aspect-ratio: 1;
  border-radius: 50%;
  border: none;
}

.meta {
  position: relative;
  flex: none;
  height: max(var(--card-meta-min), calc(50cqw - var(--card-stack-gap)));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(0.4rem, min(2.5cqw, 1.67cqh), 0.75rem);
  padding: var(--card-gutter);
  border-radius: var(--radius-normal);
  background: transparent;
}

.meta-fill {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.meta-head {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.when-skel {
  width: 55%;
  height: clamp(1.05rem, min(6cqw, 4cqh), 1.5rem);
}

.sub-skel {
  width: 70%;
  height: clamp(0.75rem, min(4cqw, 2.67cqh), 0.875rem);
}

.controls {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.primary {
  grid-column: 2;
  justify-self: center;
}

.action-button {
  width: var(--card-play-size);
  height: var(--card-play-size);
  border-radius: 50%;
}
</style>
